/**
 * Admin 대시보드·사이트·업로드·도움말·커뮤니티 매뉴얼. 라우트 admin.php 와 동기화.
 * data-controller="AdminController", data-action="dashboard" | "components" | "siteIndex" | "help" | "communityManual" | "uploadImage" | "uploadAttachment"
 * - components: DatePicker, QuillEditor 래퍼 사용 (SlimSelect는 initAdminSelects에서 SelectWrapper로 처리)
 * - help / communityManual: .admin__main 스크롤 컨테이너 기준으로 좌·우 목차 GSAP ScrollTrigger pin
 * - communityManual: 탭 전환 및 해시 기반 목차 링크
 */
import { Logger } from "../../utils/Logger";
import { DatePicker } from "../../components/DatePicker";
import { QuillEditor } from "../../components/QuillEditor";
import { RichEditor } from "../../components/RichEditor";
import { AdminGuideController } from "./AdminGuideController";

const COMMUNITY_MANUAL_IDS = [
    "comm-flow",
    "comm-notice",
    "comm-fan",
    "comm-exclusive",
    "comm-post",
    "comm-comment",
    "comm-profile",
];

function noop(action: string): void {
    Logger.debug(`AdminController.${action} (no-op)`);
}

function setActiveDocTab(tabId: string, tabs: Element[], panels: Element[]): void {
    tabs.forEach((t) => {
        t.classList.toggle("is-active", t.getAttribute("data-tab") === tabId);
    });
    panels.forEach((p) => {
        p.classList.toggle("is-active", p.getAttribute("data-tab-panel") === tabId);
    });
}

const ADMIN_DOCS_BREAKPOINT = 992;

/**
 * 도움말/매뉴얼 문서 페이지: 스크롤 컨테이너(.admin__main) 기준으로 좌·우 목차를 ScrollTrigger로 pin.
 * cursor-execution-rules: 레이아웃에서 GSAP 로드, 해당 컨트롤러 메소드에서만 pin 초기화.
 * 반응형: 좁은 화면(단일 컬럼)에서는 pin 미적용.
 */
function initDocsScrollPin(): void {
    const scroller = document.querySelector<HTMLElement>(".admin__main");
    const nav = document.querySelector<HTMLElement>(".admin-docs__nav");
    const toc = document.querySelector<HTMLElement>(".admin-docs__toc");
    if (!scroller || !nav || !toc || window.innerWidth < ADMIN_DOCS_BREAKPOINT) return;

    const gsap = (window as Window & { gsap?: { registerPlugin: (p: unknown) => void } }).gsap;
    const ST = (window as Window & { ScrollTrigger?: { create: (config: object) => void } }).ScrollTrigger;
    if (!gsap || !ST) return;
    gsap.registerPlugin(ST);

    ST.create({
        trigger: nav,
        pin: true,
        scroller,
        start: "top top",
        end: "bottom top",
    });
    ST.create({
        trigger: toc,
        pin: true,
        scroller,
        start: "top top",
        end: "bottom top",
    });
}

export const AdminController = {
    dashboard(): void {
        noop("dashboard");
    },
    components(): void {
        // Datetime / Time pickers — handled globally by initAdminFlatpickrPickers()

        const editorWrap = document.getElementById("demo-editor-wrap");
        if (editorWrap) {
            new QuillEditor(editorWrap, {
                placeholder: "내용을 입력하세요.",
                toolbar: [
                    ["bold", "italic"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["link"],
                ],
            }).init();
        }

        // Inline calendars (Flatpickr)
        const fp = (window as unknown as { flatpickr?: (el: HTMLElement, opts: Record<string, unknown>) => void }).flatpickr;
        if (typeof fp !== "function") return;

        const inlineEl = document.querySelector<HTMLElement>("[data-calendar-inline]");
        if (inlineEl) fp(inlineEl, { inline: true, locale: "ko" });

        const rangeEl = document.querySelector<HTMLElement>("[data-calendar-range]");
        if (rangeEl) fp(rangeEl, { inline: true, mode: "range", locale: "ko" });

        const monthEl = document.querySelector<HTMLElement>("[data-calendar-month]");
        if (monthEl) fp(monthEl, { inline: true, plugins: [], locale: "ko", showMonths: 1 });
    },
    siteIndex(): void {
        noop("siteIndex");
    },
    help(): void {
        initDocsScrollPin();
    },
    communityManual(): void {
        const tabs = Array.from(document.querySelectorAll(".admin-docs__tab"));
        const panels = Array.from(document.querySelectorAll(".admin-docs__tab-panel"));
        if (!tabs.length || !panels.length) return;

        const checkHash = (): void => {
            const hash = (window.location.hash || "").replace("#", "");
            if (hash === "tab-community" || COMMUNITY_MANUAL_IDS.indexOf(hash) !== -1) {
                setActiveDocTab("community", tabs, panels);
            } else if (hash === "tab-site") {
                setActiveDocTab("site", tabs, panels);
            }
        };

        tabs.forEach((tab) => {
            tab.addEventListener("click", (e) => {
                e.preventDefault();
                const id = tab.getAttribute("data-tab");
                if (id) {
                    setActiveDocTab(id, tabs, panels);
                    const panel = document.getElementById("tab-" + id);
                    if (panel) panel.scrollIntoView({ behavior: "smooth", block: "start" });
                }
            });
        });

        document.querySelectorAll(".admin-docs__nav-link[href^='#']").forEach((link) => {
            link.addEventListener("click", () => {
                const href = link.getAttribute("href");
                if (href && href.length > 1) {
                    const id = href.replace("#", "");
                    if (COMMUNITY_MANUAL_IDS.indexOf(id) !== -1) {
                        setActiveDocTab("community", tabs, panels);
                    } else {
                        setActiveDocTab("site", tabs, panels);
                    }
                }
            });
        });

        window.addEventListener("hashchange", checkHash);
        checkHash();
        initDocsScrollPin();
    },
    uploadImage(): void {
        noop("uploadImage");
    },
    uploadAttachment(): void {
        noop("uploadAttachment");
    },
    /**
     * 가이드 — 브랜딩 기획서 (/console/guide/brand)
     * 좌측 사이드바 + 우측 미니 TOC scroll-spy + 앵커 스무스 스크롤.
     */
    guideBrand(): void {
        AdminGuideController.mountShell();
    },
    /**
     * 가이드 — 스타일 가이드 (/console/guide/style)
     */
    guideStyle(): void {
        AdminGuideController.mountShell();
    },
    /**
     * 가이드 — 컴포넌트 가이드 (/console/guide/components)
     * 라이브 프리뷰 + 코드 토글 + 클립보드 복사 + Rich Editor 자동 마운트.
     */
    guideComponents(): void {
        AdminGuideController.mountShell();
        RichEditor.initAll();
    },
};
